import { memo } from "react";
import { Link as RouterLink } from "react-router-dom";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getDocument, ScorecardPayloadView, type QueueRow } from "../../../../lib/corpusApi";
import { DocumentMetadataView } from "./documentMetadataView";
import { useAsyncData } from "../../../../hooks/useAsyncData";
import { ExternalLink } from "../../../../components/primitives/data-display/ExternalLink";
import { TierClearedBadges } from "../../../../components/domain/data-display/TierClearedBadges";
import { TaxonomyUrnBadge } from "../../../../components/domain/data-display/TaxonomyUrnBadge";
import { AnnotatedFieldsSummary } from "../../../../components/domain/annotation/FieldReviewPanel/AnnotatedFieldsSummary";
import { Note } from "../../../../components/primitives/data-display/Note";
import { SectionLabel } from "../../../../components/primitives/data-display/SectionLabel";
import { gaps } from "../../../../components/primitives/spacing";
import { documentTracePath } from "../../../../route_paths";

export interface DocumentDetailProps {
  sourceId: string;
}

const MetaRow = memo(function MetaRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value) return null;
  return (
    <Stack direction="row" gap={gaps.snug} sx={{ alignItems: "baseline" }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
});

const DocumentMeta = memo(function DocumentMeta({ doc }: { doc: QueueRow }) {
  const meta = new DocumentMetadataView(doc);

  if (!meta.hasAny) return null;

  return (
    <Stack gap={gaps.snug}>
      <SectionLabel>Document</SectionLabel>
      <Stack gap={1}>
        {meta.sourceType && (
          <Stack direction="row" gap={gaps.snug} sx={{ alignItems: "center" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 100, flexShrink: 0 }}
            >
              Source type
            </Typography>
            <Chip label={meta.sourceType} size="small" variant="outlined" />
          </Stack>
        )}
        <MetaRow label="Authors" value={meta.authors} />
        <MetaRow label="Journal" value={meta.journal} />
        <MetaRow label="Published" value={meta.publishedDate} />
        {meta.doi && (
          <Stack direction="row" gap={gaps.snug} sx={{ alignItems: "baseline" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 100, flexShrink: 0 }}
            >
              DOI
            </Typography>
            <ExternalLink href={`https://doi.org/${meta.doi}`} variant="body2">
              {meta.doi}
            </ExternalLink>
          </Stack>
        )}
        {meta.fields.length > 0 && (
          <Stack direction="row" gap={gaps.snug} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 100, flexShrink: 0 }}
            >
              Fields
            </Typography>
            <Stack direction="row" gap={0.5} sx={{ flexWrap: "wrap" }}>
              {meta.fields.map((urn) => (
                <TaxonomyUrnBadge key={urn} urn={urn} />
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
});

export interface DocumentDetailBodyProps {
  doc: QueueRow;
}

/** Presentational body: metadata, scorecard, and a link to the document's trace (how it was
 *  resolved, parsed and admitted) for an already-loaded document. */
export const DocumentDetailBody = memo(function DocumentDetailBody({
  doc,
}: DocumentDetailBodyProps) {
  const scorecard = new ScorecardPayloadView(doc.ai_scorecard).effectiveScorecard;
  return (
    <Stack gap={3}>
      <DocumentMeta doc={doc} />
      <Divider />
      <Stack gap={gaps.snug}>
        <SectionLabel>Scorecard</SectionLabel>
        <TierClearedBadges status={doc.tier_cleared} confidenceScores={doc.confidence_scores} />
        <AnnotatedFieldsSummary scorecard={scorecard} annotation={doc.annotation} />
      </Stack>
      <Divider />
      <Link component={RouterLink} to={documentTracePath(doc.id)} variant="body2">
        View extraction &amp; admission trace →
      </Link>
    </Stack>
  );
});

/** Fetches a document then renders DocumentDetailBody. Mounts fresh per open via conditional mount. */
export const DocumentDetail = memo(function DocumentDetail({ sourceId }: DocumentDetailProps) {
  const { data, loading, error } = useAsyncData(() => getDocument(sourceId));

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  if (error) return <Note padding={2}>{error}</Note>;
  if (!data) return null;

  return <DocumentDetailBody doc={data} />;
});
